import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from './api';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api/url-availability-exceptions`;

export type UrlExceptions = {
  _id?: string;
  url: string;
  siteCode?: string;
  includeSubPages?: boolean;
  from?: string;
  to?: string;
  createdAt?: string;
  updatedAt?: string;
};

type GetUrlExceptionsResponse = {
  items: UrlExceptions[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  lastUpdated?: string;
};

export type BulkOperationsPayload = {
  create?: Omit<UrlExceptions, '_id' | 'createdAt' | 'updatedAt'>[];
  delete?: string[];
};

export type BulkOperationsResponse = {
  message: string;
  result: {
    created: number;
    deleted: number;
    totalOperations: number;
  };
  data: {
    created: UrlExceptions[];
    deleted: string[];
  };
};

export type HistoryRecord = {
  _id: string;
  url: string;
  siteCode?: string;
  includeSubPages?: boolean;
  from?: string;
  to?: string;
  operation: 'add' | 'remove';
  userEmail: string;
  createdAt: string;
};

export type HistoryResponse = {
  items: HistoryRecord[];
  totalCount: number;
  totalPages: number;
};

export const apiUrlExceptions = createApi({
  reducerPath: 'apiUrlExceptions',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: BASE_URL }),
  tagTypes: ['urlExceptions', 'urlExceptionsOperationsHistory'],
  endpoints: (build) => ({
    getUrlExceptions: build.query<GetUrlExceptionsResponse, { page?: number; limit?: number }>({
      query: ({ page = 1, limit }) => {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        if (limit !== undefined) {
          params.append('limit', limit.toString());
        }

        return {
          url: `/?${params.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['urlExceptions']
    }),
    bulkUpdateUrlExceptions: build.mutation<BulkOperationsResponse, BulkOperationsPayload>({
      query: (body) => ({
        url: '/bulk-operations',
        method: 'POST',
        body
      }),
      invalidatesTags: ['urlExceptions', 'urlExceptionsOperationsHistory']
    }),
    getHistory: build.query<
      HistoryResponse,
      { page?: number; limit?: number; fromDate?: string; toDate?: string; url?: string; userEmail?: string }
    >({
      query: ({ page = 1, limit = 10, fromDate, toDate, url, userEmail }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString()
        });

        if (fromDate) {
          params.append('fromDate', fromDate);
        }
        if (toDate) {
          params.append('toDate', toDate);
        }
        if (url) {
          params.append('url', url);
        }
        if (userEmail) {
          params.append('userEmail', userEmail);
        }

        return {
          url: `/history?${params.toString()}`,
          method: 'GET'
        };
      },
      providesTags: ['urlExceptionsOperationsHistory']
    }),
    getUserEmails: build.query<string[], void>({
      query: () => ({
        url: '/history/users',
        method: 'GET'
      }),
      providesTags: ['urlExceptionsOperationsHistory']
    })
  })
});

export const {
  useGetUrlExceptionsQuery,
  useBulkUpdateUrlExceptionsMutation,
  useGetHistoryQuery,
  useGetUserEmailsQuery
} = apiUrlExceptions;
